import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, AppSyncApi, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { MonitoringService } from '../../core/services/monitoring.service';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'apis' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'count', label: 'Reqs'    },
  { key: 'e5xx',  label: '5XX'     },
  { key: 'e4xx',  label: '4XX'     },
  { key: 'lat',   label: 'Latency' },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['auth', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'auth',
    label: 'Auth',
    options: [
      { value: 'API_KEY',           label: 'API key'      },
      { value: 'AWS_IAM',           label: 'IAM'          },
      { value: 'AMAZON_COGNITO_USER_POOLS', label: 'Cognito' },
      { value: 'OPENID_CONNECT',    label: 'OIDC'         },
      { value: 'AWS_LAMBDA',        label: 'Lambda'       },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * AppSync monitoring is at the GraphQL API level (CloudWatch GraphQLAPIId
 * dimension) — 5XX/4XX error rates and request latency. The monitored resource
 * identifier is the API id. Per-resolver monitoring is not supported in v1.
 */
@Component({
  selector: 'app-appsync-detail',
  templateUrl: './appsync-detail.component.html',
})
export class AppSyncDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  apis: AppSyncApi[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview'     },
    { id: 'apis',     label: 'GraphQL APIs' },
    { id: 'activity', label: 'Activity'     },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'appsync', name: 'AppSync', icon: 'workflow', route: '/cloud/appsync', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('appsync').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the API list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getAppSyncApis(bypass).subscribe(apis => {
      this.apis       = apis;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'APPSYNC'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'apis';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts ─────────────────────────────────────────────────────────

  get totalCount():     number { return this.apis.length; }
  get monitoredCount(): number { return this.apis.filter(a => this.isMonitored(a.apiId)).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredApis(): AppSyncApi[] {
    let result = this.apis;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.apiId.toLowerCase().includes(q),
    );

    const auths = this.filters.facets['auth'] ?? [];
    if (auths.length) result = result.filter(a => auths.includes(a.authenticationType));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(a => {
        const mon = this.isMonitored(a.apiId);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(apis: AppSyncApi[], sort: string): AppSyncApi[] {
    return [...apis].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.apiId) ? 0 : 1;
          const bM = this.isMonitored(b.apiId) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(apiId: string): boolean { return this.cloud.isMonitored('appsync', apiId); }

  toggleMonitoring(apiId: string): void {
    const current = this.cloud.isMonitored('appsync', apiId);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('appsync', apiId, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(apiId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: apiId }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(apiId: string): void  { this.drawerItemId = apiId; }
  closeItemSettings(): void              { this.drawerItemId = null; }

  itemOverrideSummary(apiId: string): string {
    return this.cloud.getItemOverrideSummary('appsync', apiId);
  }
}
